import { buildFluidForm, getChatOptionsFromConfig } from "../../../shared";
import { useEffect, useState } from "react";
import { store, useStoreSelect } from "./Store";
import _ from "lodash";
import { OperationQueue } from "./OperationQueue";
import { useFormInstance } from "./FormContext";
import { defaultFormInstanceDeps } from "./FormInstance.deps";
export function useRegisterFormInstance(props = {}) {
    let [instanceId, setInstanceId] = useState(() => registerFormInstance({ form: props.form, instanceId: props.instanceId, signature: props.signature, deps: props.deps }));
    return instanceId;
}
export function registerFormInstance({ form, instanceId, signature, reset = false, deps } = {}) {
    instanceId = instanceId || `formInstance-${Object.keys(store.getSnapshot()).length}`;
    let instance = store.getSnapshot()[instanceId];
    if (instance && !reset) {
        if (form) {
            instance.setForm(form);
        }
        return instanceId;
    }
    instance = new FormInstanceService(instanceId, { form, signature, deps });
    if (form) {
        instance.setForm(form);
    }
    store.register(instanceId, instance);
    return instanceId;
}
export function deregisterFormInstance(instanceId) {
    store.unregister(instanceId);
}
export function getFormInstance(instanceId) {
    return store.getSnapshot()[instanceId];
}
export function useFormInstanceSelect(selector, instanceId) {
    let formInstanceId = useFormInstance();
    //Overrides if the instanceId is provided
    if (instanceId) {
        formInstanceId = instanceId;
    }
    return useStoreSelect(formInstanceId, selector);
}
export function useFormInstanceDispatch(formInstanceId) {
    return (action, ...args) => {
        store.dispatch(formInstanceId, action, ...args);
    };
}
export function useFormInstanceListener(instanceId, listener) {
    useEffect(() => {
        return store.subscribe(instanceId, listener);
    }, []);
}
export function useFormListener(eventName, listener) {
    let instanceId = useFormInstance();
    useFormInstanceListener(instanceId, (event) => {
        if (event.type == eventName) {
            let args = [...event.value, { form: getFormInstance(instanceId).getFluidForm(), instanceId: instanceId }];
            listener(...args);
        }
    });
}
export function useFormUpdatedListener(instanceId, listener) {
    useFormInstanceListener(instanceId, (event) => {
        if (event.type == "onFormUpdated") {
            let instance = getFormInstance(instanceId);
            listener(instance.getFormSubmission());
        }
    });
}
export class FormInstanceService {
    instanceId;
    formKey;
    submissionId;
    submissionStage;
    confirmedFields = new Set();
    fluidForm;
    client;
    lastSaved;
    isSubmitting = false;
    submissionInitialized;
    formError = null;
    actionError = null;
    chatbotService;
    deps;
    operationQueue = new OperationQueue();
    static UPDATE_FIELD_TIMEOUT = 1000;
    debouncedSaveSubmission = _.debounce(() => {
        if (this.isSubmitting) {
            return;
        }
        const data = this.fluidForm?.getFormSubmission();
        if (JSON.stringify(data) != this.lastSaved) {
            this.client.saveSubmission(this.formKey, this.submissionId, data);
            this.lastSaved = JSON.stringify(data) || "";
        }
    }, 1000, { leading: false, trailing: true });
    constructor(instanceId, { form, signature, deps }) {
        this.instanceId = instanceId;
        this.deps = { ...defaultFormInstanceDeps(), ...deps };
        this.client = this.deps.buildClient(signature);
        this.chatbotService = this.deps.buildChatbotService(this.client, this.chatbotDispatcher.bind(this), this.formKey, this.submissionId, "chat");
        if (form) {
            this.setForm(form);
        }
    }
    chatbotDispatcher(action, ...args) {
        this.dispatch(action, ...args);
        if (action === "chatbotService.messageSent") {
            this.postMessageToParent("chat:messageSent", { message: args[0] });
        }
        else if (action === "chatbotService.messageReceived") {
            this.postMessageToParent("chat:messageReceived", { message: args[0] });
        }
        else if (action === "chatbotService.messageError") {
            this.postMessageToParent("chat:messageError");
        }
    }
    postMessageToParent(type, payload = {}) {
        if (typeof window === "undefined" || window.parent === window) {
            return;
        }
        window.parent.postMessage({ source: "fluidform", type, formKey: this.formKey, submissionId: this.submissionId, ...payload }, "*");
    }
    confirmField(field) {
        this.confirmedFields.add(field);
        this.fluidForm?.clearFieldValidationResult(field);
        this.dispatch("setValidationResults", this.fluidForm?.validate());
    }
    formIsLoaded() {
        return this.fluidForm;
    }
    startAutosave() {
        if (this.fluidForm) {
            this.fluidForm.onUpdated(() => {
                this.debouncedSaveSubmission();
            });
        }
    }
    async loadForm(formKey, submissionOptions) {
        this.formKey = formKey;
        let result;
        try {
            result = await this.client.getFormConfig(formKey, submissionOptions?.submissionId);
        }
        catch (error) {
            const errorMessage = error?.message === "Form not found" ? "Form not found" : "Error loading form";
            this.dispatch("setFormErrorMessage", errorMessage);
            return;
        }
        this.dispatch("setForm", buildFluidForm(result));
        if (submissionOptions) {
            this.dispatch("initializeSubmission", submissionOptions.submissionId, submissionOptions.autoSave);
        }
    }
    getFluidForm() {
        return this.fluidForm;
    }
    getFormSubmission() {
        return this.fluidForm?.getFormSubmission();
    }
    getFormKey() {
        return this.formKey;
    }
    async initializeSubmission(submissionId, autoSave) {
        try {
            if (!this.formKey || this.submissionInitialized) {
                return;
            }
            let client = this.client;
            let result = await client.initializeSubmission(this.formKey, submissionId);
            this.submissionId = result.submissionId;
            this.setSubmissionStage(result.submission.status);
            this.setValidationResults(result.submission.validationResults);
            this.chatbotService.setThreadId(result.submissionId);
            this.chatbotService.setChatHistory(result.submission.conversation?.chatHistory);
            //The submission initialization will come empty when the submission is new
            if (!result.isNewSubmission) {
                this.dispatch("setSubmissionData", result.data);
            }
            this.dispatch("setSubmissionId", result.submissionId);
            if (autoSave) {
                this.startAutosave();
            }
            this.submissionInitialized = true;
            return result;
        }
        catch (error) {
            const errorMessage = error?.message === "Form not found" ? "Form not found" : "Something went wrong. Please reload the page.";
            this.dispatch("setFormErrorMessage", errorMessage);
        }
    }
    dispatch(action, ...args) {
        return store.dispatch(this.instanceId, action, ...args);
    }
    setSubmissionId(submissionId) {
        this.submissionId = submissionId;
        this.chatbotService.setThreadId(submissionId);
    }
    setSubmissionData(data) {
        this.fluidForm?.setSubmissionData(data);
    }
    mergeSubmissionData(data) {
        this.fluidForm?.mergeSubmissionData(data);
    }
    setForm(form) {
        if (form != this.fluidForm) {
            this.fluidForm = form;
            this.formKey = form.getFormKey();
            this.chatbotService.setFormKey(this.formKey);
            this.clearData();
            form.onUpdated((data) => {
                this.dispatch("onFormUpdated", data);
            });
            form.events.on("onValidationResultsChanged", this.instanceId, (data) => {
                this.dispatch("onValidationResultsChanged", data);
            });
            form.events.on("onBlur", this.instanceId, (data) => {
                this.dispatch("onBlur", data);
            });
            form.events.on("onFocus", this.instanceId, (data) => {
                this.dispatch("onFocus", data);
            });
        }
    }
    onValidationResultsChanged(data) {
    }
    onFormUpdated(data) {
    }
    onBlur(field) { }
    onFocus(field) { }
    setFormConfig(formConfig) {
        this.fluidForm?.setFormConfig(formConfig);
    }
    mergeAdditionalConfigValue(configKey, value) {
        this.fluidForm?.mergeAdditionalConfigValue(configKey, value);
    }
    clearData() {
        this.fluidForm?.clearValidationResults();
        this.fluidForm?.clearForm();
        this.submissionStage = undefined;
        this.confirmedFields.clear();
        this.chatbotService.setChatHistory([]);
    }
    dataCleared() {
    }
    setValidationResults(validationResults) {
        this.fluidForm?.setValidationResults(validationResults);
    }
    mergeValidationResults(validationResults) {
        this.fluidForm?.mergeValidationResults(validationResults);
    }
    setSubmissionStage(stage) {
        this.submissionStage = stage;
    }
    setFormErrorMessage(message) {
        this.formError = message;
    }
    setActionErrorMessage(message) {
        this.actionError = message;
    }
    getStylingConfig() {
        return this.fluidForm?.additionalConfigurations["styleConfig"] || {};
    }
    getActionsConfig() {
        return this.fluidForm?.additionalConfigurations["actionsConfig"] || {};
    }
    async clearForm() {
        return this.operationQueue.enqueue(async () => {
            try {
                await this.chatbotService.clearConversationHistory();
                this.fluidForm?.clearForm();
                this.confirmedFields.clear();
                this.dispatch("formCleared");
                const chatOptions = getChatOptionsFromConfig(this.fluidForm);
                const initialMessage = chatOptions.initialMessage || '';
                this.chatbotService.addInitialMessage(initialMessage, chatOptions.autoStart);
            }
            catch (error) {
                this.dispatch("setActionErrorMessage", this.deps.parseError(error));
            }
        });
    }
    formCleared() {
    }
    async startNewSubmission() {
        const isPreview = this.submissionId?.endsWith('-preview');
        if (isPreview) {
            this.clearData();
            return;
        }
        this.submissionId = undefined;
        this.submissionInitialized = false;
        this.isSubmitting = false;
        this.confirmedFields.clear();
        this.actionError = null;
        this.formError = null;
        this.clearData();
        this.chatbotService = this.deps.buildChatbotService(this.client, this.chatbotDispatcher.bind(this), this.formKey, undefined, "chat");
        await this.initializeSubmission(undefined, true);
    }
    async submit() {
        return this.operationQueue.enqueueFinal(async () => {
            const form = this.fluidForm;
            const submissionId = this.submissionId;
            const client = this.client;
            const currentStage = this.submissionStage;
            const data = form.getFormSubmission();
            if (!submissionId) {
                await this.initializeSubmission();
            }
            try {
                this.dispatch("submittingStarted");
                this.postMessageToParent("form:submitting");
                let submitResponse = await client.submitForm(form.getFormKey(), data, currentStage, submissionId, Array.from(this.confirmedFields));
                if (submitResponse.submissionId !== this.submissionId) {
                    this.dispatch("setSubmissionId", submitResponse.submissionId);
                }
                if (submitResponse.validationResultData) {
                    this.dispatch("setValidationResults", submitResponse.validationResultData);
                }
                if (submitResponse.nextStage !== this.submissionStage) {
                    this.dispatch("setSubmissionStage", submitResponse.nextStage);
                }
                this.postMessageToParent("form:submitted", { nextStage: submitResponse.nextStage });
            }
            catch (error) {
                this.dispatch("doneSubmitting");
                this.dispatch("setActionErrorMessage", this.deps.parseError(error));
            }
            finally {
                this.dispatch("doneSubmitting");
            }
        });
    }
    submittingStarted() {
        this.isSubmitting = true;
    }
    doneSubmitting() {
        this.isSubmitting = false;
    }
    helpRequest(field) { }
    async updateFields(fieldPaths) {
        return this.operationQueue.enqueue(async () => {
            if (this.isSubmitting) {
                return;
            }
            let fluidFormClient = this.client;
            let fieldPathData = this.fluidForm?.buildSubForm(fieldPaths);
            if (fieldPathData?.isEmpty()) {
                return;
            }
            let result = await fluidFormClient.updateField(this.fluidForm?.getFormKey(), this.submissionId, fieldPathData.getFormSubmissionFlat());
            this.fluidForm?.mergeSubmissionData(result.inferredData);
            this.dispatch("mergeValidationResults", result);
            return result;
        }, { timeout: FormInstanceService.UPDATE_FIELD_TIMEOUT });
    }
}
